/**
 * Idempotency keys and the reason-length rule. Spec 20.6, 21.8, 15.
 *
 * In their own module rather than beside the dialog that uses them, so the
 * dialog file exports only components — Vite's fast refresh gives up on a file
 * that mixes the two, and losing hot reload on the module's most-edited screen
 * is a real cost for no benefit.
 *
 * @category Randomization
 */

/**
 * Spec 15's minimum. Ten characters is a low bar and it is not arbitrary: it
 * stops "ok", "fix" and "." — the reasons that make an audit trail formally
 * complete and practically useless when somebody has to explain to a regulator
 * why a live randomization was reversed. Enforced here, in the DTO, and in
 * `buildAuditRow`, because each of those layers can be reached without the
 * others.
 */
export const MIN_REASON_LENGTH = 10;

/**
 * A fresh idempotency key PER SUBMISSION ATTEMPT, not per page.
 *
 * The server treats the same key against a different request body as a client
 * bug and refuses it, precisely so that a page-scoped key cannot answer the
 * second participant's randomization with the first participant's allocation.
 *
 * `crypto.randomUUID` exists in every browser this application supports; the
 * fallback covers non-secure contexts, where it is absent.
 */
export function newIdempotencyKey(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `rz-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
